import { motion, AnimatePresence } from "framer-motion";
import useAuthStore from "../../store/authStore";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAttendance,
  markAttendance,
  markRemoteAttendance,
  requestWfhPeriod,
  getWfhPeriods,
  getLeaves,
} from "../../api/attendanceService";

// ─── Timezone-safe datetime helpers ──────────────────────────────────────────
function parseISOAsLocal(isoStr) {
  if (!isoStr) return null;
  const local = isoStr.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
  return new Date(local);
}

function fmtTimeFromISO(isoStr) {
  if (!isoStr) return "—";
  const d = parseISOAsLocal(isoStr);
  if (!d || isNaN(d)) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getLocalTodayString() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// ─── IP fetch — module-level cache so it's fetched once per page load ────────
let _cachedIP = "";
let _ipFetchPromise = null;

function fetchPublicIP() {
  if (_cachedIP) return Promise.resolve(_cachedIP);
  if (_ipFetchPromise) return _ipFetchPromise;
  _ipFetchPromise = fetch("https://api.ipify.org?format=json", {
    signal: AbortSignal.timeout(4000),
  })
    .then((r) => r.json())
    .then((j) => { _cachedIP = j.ip || ""; return _cachedIP; })
    .catch(() => { _ipFetchPromise = null; return ""; });
  return _ipFetchPromise;
}

function fetchGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({}),
      { timeout: 6000, enableHighAccuracy: true }
    );
  });
}

// ─── Constants ────────────────────────────────────────────────────────────────
const HALF_DAY_LEAVE_TYPES = new Set(["HALF DAY", "HALF CASUAL"]);

// Attendance record statuses that represent calendar blocks
const CALENDAR_BLOCK_STATUSES = new Set(["HOLIDAY", "WEEK_OFF"]);

const SESSION_LABEL = {
  HALF_DAY_FIRST: "morning (first half)",
  HALF_DAY_SECOND: "afternoon (second half)",
};

const STATUS_LABEL = {
  ABSENT: "Absent",
  PRESENT: "Present",
  HALF_DAY: "Half Day",
  WORK_FROM_HOME: "WFH",
  HOLIDAY: "Holiday",
  WEEK_OFF: "Week Off",
  ON_LEAVE: "On Leave",
  REMOTE_PENDING: "Remote Pending",
};

const STATUS_STYLE = {
  ABSENT: "bg-red-500/15 border-red-500/40 text-red-400",
  PRESENT: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  HALF_DAY: "bg-orange-500/15 border-orange-500/40 text-orange-400",
  WORK_FROM_HOME: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  HOLIDAY: "bg-purple-500/15 border-purple-500/40 text-purple-300",
  WEEK_OFF: "bg-slate-500/15 border-slate-500/40 text-slate-300",
  ON_LEAVE: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  REMOTE_PENDING: "bg-yellow-500/15 border-yellow-500/40 text-yellow-300",
};

const METHOD_BADGE = {
  IP: { icon: "🌐", label: "Office IP · IP Verified", cls: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" },
  GPS: { icon: "📍", label: "GPS fallback · GPS Verified", cls: "bg-blue-500/15 border-blue-500/30 text-blue-300" },
  REMOTE: { icon: "🛰️", label: "Remote", cls: "bg-cyan-500/15 border-cyan-500/30 text-cyan-300" },
  ADMIN: { icon: "🏢", label: "Admin manual edit", cls: "bg-slate-500/15 border-slate-500/30 text-slate-300" },
};

const BLOCK_CONFIG = {
  LEAVE: {
    icon: "🏖️",
    title: "On Approved Leave",
    color: "bg-amber-500/12 border-amber-500/35 text-amber-300",
    iconBg: "bg-amber-500/20",
  },
  HOLIDAY: {
    icon: "🎉",
    title: "Public Holiday",
    color: "bg-purple-500/12 border-purple-500/35 text-purple-300",
    iconBg: "bg-purple-500/20",
  },
  WEEK_OFF: {
    icon: "😴",
    title: "Week Off",
    color: "bg-slate-500/12 border-slate-500/35 text-slate-300",
    iconBg: "bg-slate-500/20",
  },
  REMOTE_PENDING: {
    icon: "⏳",
    title: "Remote Request Pending",
    color: "bg-yellow-500/12 border-yellow-500/35 text-yellow-300",
    iconBg: "bg-yellow-500/20",
  },
  LOCATION: {
    icon: "🚫",
    title: "Location Restricted",
    color: "bg-red-500/12 border-red-500/35 text-red-300",
    iconBg: "bg-red-500/20",
  },
};

const HARD_BLOCK_TYPES = new Set(["LEAVE", "REMOTE_PENDING"]);

function calcDuration(ci, co) {
  if (!ci || !co) return "—";
  const diff = (new Date(co) - new Date(ci)) / 60000;
  if (diff < 0) return "—";
  return `${Math.floor(diff / 60)}h ${Math.floor(diff % 60)}m`;
}

// ─── Derive all display state from raw API data ───────────────────────────────
/**
 * Pure function: given today's attendance record + today's approved leave,
 * returns the display state including calendar-based blocks.
 *
 * Key logic:
 * - HOLIDAY / WEEK_OFF status on the attendance record = hard block
 *   (backend already handles overrides — overridden employees get ABSENT, not HOLIDAY/WEEK_OFF)
 * - REMOTE_PENDING = hard block
 * - ON_LEAVE with half-day leave type = show info banner, allow marking
 * - ON_LEAVE with full-day leave type = hard block
 */
function deriveState(todayRecord, todayLeave) {
  const rec = todayRecord || {};
  const status = rec.status;

  // 1. Calendar block: HOLIDAY or WEEK_OFF on the record itself.
  //    Overridden employees will have ABSENT (or PRESENT/etc.) — not HOLIDAY/WEEK_OFF —
  //    so this correctly blocks only non-overridden employees.
  if (status === "HOLIDAY") {
    return {
      hardBlock: null,
      halfDayLeave: null,
      softBlock: {
        block_type: "HOLIDAY",
        reason:
          "Today is a public holiday. If you're working today, your attendance " +
          "will be submitted for admin approval. Comp-off will be credited if approved.",
      },
    };
  }

  if (status === "WEEK_OFF") {
    return {
      hardBlock: null,
      halfDayLeave: null,
      softBlock: {
        block_type: "WEEK_OFF",
        reason:
          "Today is a week off. If you're working today, your attendance " +
          "will be submitted for admin approval. Comp-off will be credited if approved.",
      },
    };
  }

  // 2. Remote pending
  if (status === "REMOTE_PENDING") {
    if (rec.check_in && !rec.check_out) {
      return { hardBlock: null, halfDayLeave: null };
    }
    return {
      hardBlock: {
        blocked: true,
        block_type: "REMOTE_PENDING",
        reason:
          "You have already submitted a remote attendance request. Awaiting admin approval.",
      },
      halfDayLeave: null,
    };
  }

  // 3. ON_LEAVE — distinguish half-day vs full-day using the leave record
  if (status === "ON_LEAVE" || todayLeave) {
    if (todayLeave) {
      const rawType = (
        todayLeave.leave_type_raw ?? todayLeave.leave_type ?? ""
      ).toUpperCase();
      if (HALF_DAY_LEAVE_TYPES.has(rawType)) {
        return { hardBlock: null, halfDayLeave: todayLeave };
      }
      return {
        hardBlock: {
          blocked: true,
          block_type: "LEAVE",
          reason: `You are on approved ${todayLeave.leave_type} today. Attendance cannot be marked on approved leave days.`,
        },
        halfDayLeave: null,
      };
    }
  }

  return { hardBlock: null, halfDayLeave: null, softBlock: null };
}

// ─── SoftBlockBanner ─────────────────────────────────────────────────────────
function SoftBlockBanner({ softBlock, onAcknowledge }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="mb-4 rounded-2xl p-4 border"
      style={{
        background: "rgba(245,158,11,0.10)",
        borderColor: "rgba(245,158,11,0.35)",
      }}
    >
      <div className="flex items-start gap-3 mb-3">
        <span className="text-xl flex-shrink-0">🗓️</span>
        <div>
          <p className="text-amber-300 font-semibold text-sm mb-1">
            {softBlock.block_type === "HOLIDAY" ? "Public Holiday" : "Week Off"}
          </p>
          <p className="text-amber-200/70 text-xs leading-relaxed">
            {softBlock.reason}
          </p>
        </div>
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={onAcknowledge}
        className="w-full py-2.5 rounded-xl font-semibold text-sm text-white
          bg-gradient-to-r from-amber-500 to-orange-500
          hover:from-amber-400 hover:to-orange-400 shadow-lg transition"
      >
        ✅ I'm Working Today
      </motion.button>
    </motion.div>
  );
}

// ─── Atoms ────────────────────────────────────────────────────────────────────
function SummaryCard({ icon, label, value }) {
  return (
    <div className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10 text-center">
      <div className="text-lg sm:text-xl mb-1">{icon}</div>
      <div className="text-[10px] sm:text-xs text-white/50 mb-0.5 leading-tight">{label}</div>
      <div className="text-white font-bold text-xs sm:text-sm">{value}</div>
    </div>
  );
}

function ActionBtn({ label, color, onClick, disabled }) {
  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.03 }}
      whileTap={{ scale: disabled ? 1 : 0.97 }}
      onClick={onClick}
      disabled={disabled}
      className={`w-full sm:w-44 md:w-48 py-3 sm:py-4 rounded-xl font-bold text-white text-sm sm:text-base
        bg-gradient-to-r ${color} shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition`}
    >
      {label}
    </motion.button>
  );
}

function LockedBtn({ label, title = "" }) {
  return (
    <div
      title={title}
      className="w-full sm:w-44 md:w-48 py-3 sm:py-4 px-4 rounded-xl bg-white/5 border border-white/10
        text-center select-none cursor-not-allowed"
    >
      <span className="text-white/35 font-semibold text-xs sm:text-sm">{label}</span>
    </div>
  );
}

function BlockBanner({ blockType, reason }) {
  const cfg = BLOCK_CONFIG[blockType] || BLOCK_CONFIG.LOCATION;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border p-4 sm:p-5 ${cfg.color}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center
            flex-shrink-0 text-lg sm:text-xl ${cfg.iconBg}`}
        >
          {cfg.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm sm:text-base leading-tight mb-1">{cfg.title}</p>
          <p className="text-xs sm:text-sm leading-relaxed opacity-80">{reason}</p>
        </div>
      </div>
    </motion.div>
  );
}

function HalfDayLeaveBanner({ leave }) {
  if (!leave) return null;
  const sessionLabel = SESSION_LABEL[leave.half_day_session] || "half day";
  const typeLabel =
    (leave.leave_type_raw ?? "").toUpperCase() === "HALF CASUAL"
      ? "Half Day Casual"
      : "Half Day";
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border p-4 sm:p-5 bg-amber-500/10 border-amber-500/30 text-amber-200"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center
            flex-shrink-0 text-lg sm:text-xl bg-amber-500/20"
        >
          🕐
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm sm:text-base leading-tight mb-1">
            {typeLabel} —{" "}
            {sessionLabel === "morning (first half)" ? "Morning" : "Afternoon"} Leave
          </p>
          <p className="text-xs sm:text-sm leading-relaxed opacity-80">
            You have approved {typeLabel.toLowerCase()} for the{" "}
            <strong>{sessionLabel}</strong>. You can still mark attendance for
            the remaining half of the day.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  REMOTE MODAL
// ──────────────────────────────────────────────────────────────────────────────
function RemoteModal({ action, today, onClose, onSuccess }) {
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) { setError("Please provide a reason."); return; }
    if (!fromDate || !toDate) { setError("Please select From and To dates."); return; }
    if (toDate < fromDate) { setError("End date cannot be before start date."); return; }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const isTodayOnly = fromDate === today && toDate === today;
      if (isTodayOnly) {
        setStep("locating");
        const [gps, ip] = await Promise.all([fetchGPS(), fetchPublicIP()]);
        setStep("submitting");
        const payload = { action, remote_reason: trimmed };
        if (ip) payload.client_ip_override = ip;
        if (gps.latitude != null && gps.longitude != null) {
          payload.latitude = gps.latitude;
          payload.longitude = gps.longitude;
        }
        await markRemoteAttendance(payload);
        setSuccess("Remote request submitted!");
        setTimeout(() => onSuccess(), 1000);
      } else {
        setStep("submitting");
        await requestWfhPeriod({ from_date: fromDate, to_date: toDate, reason: trimmed });
        setSuccess("WFH period request submitted! Awaiting approval.");
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (err) {
      const d = err?.response?.data;
      const msg =
        d?.blocked && d?.reason
          ? d.reason
          : d?.error
            ? d.error
            : d?.detail
              ? d.detail
              : d?.non_field_errors?.[0]
                ? d.non_field_errors[0]
                : typeof d === "string"
                  ? d
                  : "Failed to submit request. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
      setStep("");
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [submitting, onClose]);

  const stepLabel =
    step === "locating"
      ? "📍 Getting your location…"
      : step === "submitting"
        ? "🛰️ Submitting request…"
        : "";
  const actionLabel = action === "check_in" ? "Check In" : "Check Out";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center
        bg-black/70 backdrop-blur-sm px-0 sm:px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 40 }}
        transition={{ type: "spring", damping: 24, stiffness: 280 }}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: "linear-gradient(150deg,#0d1b35 0%,#111e38 100%)" }}
      >
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>
        <div className="h-[3px] bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500" />

        <div className="p-5 sm:p-7 pt-4">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-cyan-500
                to-blue-600 flex items-center justify-center text-lg sm:text-xl shadow-lg flex-shrink-0"
              >
                🛰️
              </div>
              <div>
                <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                  Remote Attendance
                </h3>
                <p className="text-cyan-400/80 text-xs font-medium mt-0.5">
                  {actionLabel} · Off-Site
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className="w-7 h-7 rounded-full flex items-center justify-center
                text-white/30 hover:text-white/70 hover:bg-white/10 transition text-sm disabled:opacity-40"
            >
              ✕
            </button>
          </div>

          <div
            className="mb-4 sm:mb-5 px-3 py-2.5 sm:px-3.5 sm:py-3 rounded-xl flex gap-2.5"
            style={{
              background: "rgba(59,130,246,0.10)",
              border: "1px solid rgba(59,130,246,0.22)",
            }}
          >
            <span className="text-blue-400 text-sm flex-shrink-0 mt-0.5">ℹ️</span>
            <p className="text-blue-200/80 text-xs leading-relaxed">
              Submit a remote attendance request. You can request for today only
              or a range of dates (WFH Period).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1.5">
                From Date
              </p>
              <input
                type="date"
                value={fromDate}
                min={today}
                onChange={(e) => { setFromDate(e.target.value); setError(""); }}
                disabled={submitting}
                className="w-full rounded-xl px-3 py-2.5 text-sm text-white font-medium focus:outline-none transition disabled:opacity-50"
                style={{
                  background: "rgba(15,30,60,0.85)",
                  border: "1.5px solid rgba(99,179,237,0.35)",
                  caretColor: "#67e8f9",
                }}
              />
            </div>
            <div>
              <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1.5">
                To Date
              </p>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => { setToDate(e.target.value); setError(""); }}
                disabled={submitting}
                className="w-full rounded-xl px-3 py-2.5 text-sm text-white font-medium focus:outline-none transition disabled:opacity-50"
                style={{
                  background: "rgba(15,30,60,0.85)",
                  border: "1.5px solid rgba(99,179,237,0.35)",
                  caretColor: "#67e8f9",
                }}
              />
            </div>
          </div>

          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-3 px-3.5 py-2.5 rounded-xl text-emerald-300 text-xs font-medium flex items-center gap-2"
                style={{
                  background: "rgba(16,185,129,0.1)",
                  border: "1px solid rgba(16,185,129,0.3)",
                }}
              >
                <span>✅</span>
                <span>{success}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-2">
            Reason
          </p>
          <textarea
            ref={textareaRef}
            value={reason}
            onChange={(e) => { setReason(e.target.value.slice(0, 500)); setError(""); }}
            rows={3}
            disabled={submitting}
            placeholder="e.g. Site visit, health reasons, permanent WFH..."
            style={{
              width: "100%",
              background: "rgba(15,30,60,0.85)",
              color: "#e2e8f0",
              border: "1.5px solid rgba(99,179,237,0.35)",
              borderRadius: "12px",
              padding: "12px 16px",
              fontSize: "14px",
              lineHeight: "1.6",
              resize: "none",
              outline: "none",
              caretColor: "#67e8f9",
              transition: "border-color 0.2s, box-shadow 0.2s",
              display: "block",
              marginBottom: "4px",
              opacity: submitting ? 0.5 : 1,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "rgba(99,179,237,0.75)";
              e.target.style.boxShadow = "0 0 0 3px rgba(99,179,237,0.13)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "rgba(99,179,237,0.35)";
              e.target.style.boxShadow = "none";
            }}
          />
          <p
            className={`text-xs text-right mb-3 ${reason.length > 450 ? "text-orange-400" : "text-white/25"
              }`}
          >
            {reason.length} / 500
          </p>

          <AnimatePresence>
            {submitting && stepLabel && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-3 px-3.5 py-2.5 rounded-xl text-cyan-300 text-xs font-medium flex items-center gap-2"
                style={{
                  background: "rgba(6,182,212,0.1)",
                  border: "1px solid rgba(6,182,212,0.3)",
                }}
              >
                <span className="w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                {stepLabel}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-3 px-3.5 py-2.5 rounded-xl text-red-300 text-xs font-medium flex items-start gap-2"
                style={{
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.3)",
                }}
              >
                <span className="flex-shrink-0 mt-0.5">⚠️</span>
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl text-white/60 hover:text-white text-sm font-medium transition disabled:opacity-40"
              style={{ border: "1px solid rgba(255,255,255,0.15)" }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !reason.trim()}
              className="flex-1 py-3 rounded-xl font-semibold text-sm text-white
                bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500
                disabled:opacity-40 disabled:cursor-not-allowed shadow-lg transition-all"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {step === "locating" ? "Locating…" : "Submitting…"}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>🛰️</span> Submit
                </span>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  ATTENDANCE WIDGET
// ──────────────────────────────────────────────────────────────────────────────
function AttendanceWidget({ role, today, navigate }) {
  const [data, setData] = useState({
    record: null,
    todayLeave: null,
    activeWfhPeriod: null,
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [showRemote, setShowRemote] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [verifyStep, setVerifyStep] = useState("");
  const [holidayAcknowledged, setHolidayAcknowledged] = useState(false);
  const [holidayPendingToast, setHolidayPendingToast] = useState(false);

  // ── Single parallel load ──────────────────────────────────────────────────
  const loadAll = useCallback(
    async (isBackground = false) => {
      if (!isBackground) setLoading(true);
      try {
        const [attRes, leavesRes, wfhRes] = await Promise.all([
          getAttendance(),
          getLeaves({ date: today }),
          getWfhPeriods({ status: "APPROVED" }),
        ]);

        const list = attRes.data.attendances || [];
        const record =
          list.find(
            (a) => (typeof a.date === "string" ? a.date : "") === today
          ) || null;

        const leaves = leavesRes.data.leaves || [];
        const todayLeave =
          leaves.find((l) => {
            if (l.status !== "Approved") return false;
            return l.leave_date_from <= today && l.leave_date_to >= today;
          }) || null;

        const todayDate = new Date(today);
        const activeWfhPeriod =
          (wfhRes.data.wfh_periods || []).find((p) => {
            return (
              new Date(p.from_date) <= todayDate &&
              todayDate <= new Date(p.to_date)
            );
          }) || null;

        setData({ record, todayLeave, activeWfhPeriod, holiday_work_pending: !!attRes.data.holiday_work_pending });
      } catch (err) {
        console.error("Attendance data load failed:", err);
      } finally {
        if (!isBackground) setLoading(false);
      }
    },
    [today]
  );

  useEffect(() => {
    loadAll();
    fetchPublicIP();
  }, [loadAll]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") loadAll(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadAll]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") loadAll(true);
    }, 60_000);
    return () => clearInterval(id);
  }, [loadAll]);

  // ── Derive display state ──────────────────────────────────────────────────
  const { record, todayLeave, activeWfhPeriod } = data;
  const { hardBlock, halfDayLeave, softBlock } = deriveState(record, todayLeave);
  // Reset acknowledgement when record changes (e.g. after check-in)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isSoftBlocked = !!softBlock && !holidayAcknowledged;

  // ── Mark attendance ───────────────────────────────────────────────────────
  const handleMark = async (action) => {
    setActionLoading(true);
    setLocationError(null);
    setPendingAction(action);
    try {
      setVerifyStep("ip");
      const ip = await fetchPublicIP();
      let res;
      let ipBlocked = false;
      try {
        res = await markAttendance({ action, client_ip_override: ip });
      } catch (err) {
        const d = err?.response?.data;
        if (d?.blocked) {
          if (d.block_type && HARD_BLOCK_TYPES.has(d.block_type)) {
            await loadAll(true);
            return;
          }
          ipBlocked = true;
        } else {
          setLocationError(d?.error || d?.detail || "Something went wrong.");
          return;
        }
      }

      if (!ipBlocked) {
        if (res?.data?.blocked) {
          const d = res.data;
          if (d.block_type && HARD_BLOCK_TYPES.has(d.block_type)) {
            await loadAll(true);
          } else {
            setLocationError(d.reason);
          }
          return;
        }
        // Show holiday work request toast on check-out
        if (res?.data?.holiday_work_pending) {
          setHolidayPendingToast(true);
          setTimeout(() => setHolidayPendingToast(false), 7000);
        }
        await loadAll(true);
        return;
      }

      // IP blocked → try GPS
      setVerifyStep("gps");
      const gps = await fetchGPS();
      if (!gps.latitude || !gps.longitude) {
        setLocationError(
          "Your IP is not on the office allowlist and location access is unavailable. " +
          "Enable location permissions and try again, or use Remote Attendance."
        );
        return;
      }

      try {
        res = await markAttendance({
          action,
          client_ip_override: ip,
          latitude: gps.latitude,
          longitude: gps.longitude,
        });
      } catch (err) {
        const d = err?.response?.data;
        if (d?.blocked && d?.block_type && HARD_BLOCK_TYPES.has(d.block_type)) {
          await loadAll(true);
        } else {
          setLocationError(
            d?.blocked
              ? d.reason
              : d?.error || d?.detail || "Something went wrong."
          );
        }
        return;
      }

      if (res?.data?.blocked) {
        const d = res.data;
        if (d.block_type && HARD_BLOCK_TYPES.has(d.block_type)) {
          await loadAll(true);
        } else {
          setLocationError(d.reason);
        }
        return;
      }

      // Show holiday work request toast on check-out
      if (res?.data?.holiday_work_pending) {
        setHolidayPendingToast(true);
        setTimeout(() => setHolidayPendingToast(false), 7000);
      }
      await loadAll(true);
    } finally {
      setActionLoading(false);
      setVerifyStep("");
    }
  };

  const openRemoteModal = () => {
    const action = !record?.check_in ? "check_in" : "check_out";
    setPendingAction(action);
    setShowRemote(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const rec = record || {};
  const checkedIn = !!rec.check_in;
  const checkedOut = !!rec.check_out;
  const isHardBlocked = !!hardBlock;
  const isCalendarBlock = CALENDAR_BLOCK_STATUSES.has(rec.status);
  const methodBadge = rec.method ? METHOD_BADGE[rec.method] : null;
  const isLocationErrored = !!locationError && !isHardBlocked;

  // Remote button: hidden when hard-blocked OR when already fully checked in+out
  const showRemoteButton = !isHardBlocked && !isSoftBlocked && !(checkedIn && checkedOut);
  const displayStatus = rec.status || "ABSENT";

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl mx-auto px-2 sm:px-0 h-full flex flex-col"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-6 md:p-8 shadow-2xl h-full flex flex-col justify-between">

          {/* Header */}
          <div className="flex items-center justify-between mb-5 sm:mb-8">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br
                from-emerald-500 to-teal-600 flex items-center justify-center text-lg sm:text-xl shadow-lg flex-shrink-0"
              >
                ✅
              </div>
              <div className="flex flex-col">
                <h3 className="text-base sm:text-2xl font-bold text-white leading-tight">
                  Mark Attendance
                </h3>
                <p className="text-[10px] sm:text-xs text-white/50 mt-0.5">
                  {role === "MANAGER"
                    ? "Same restrictions apply as employees."
                    : "Record your daily attendance"}
                </p>
              </div>
            </div>
            {/* <button
              onClick={() => navigate(`/${role.toLowerCase()}/attendance-report`)}
              className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold
                bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-md
                hover:opacity-90 transition whitespace-nowrap"
            >
              📊 <span className="hidden xs:inline">View </span>Report
            </button> */}
          </div>

          {/* Active WFH Period Badge */}
          <AnimatePresence>
            {activeWfhPeriod && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6 px-4 py-3 rounded-2xl bg-violet-500/10 border border-violet-500/25
                  flex items-center justify-between shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📅</span>
                  <div>
                    <p className="text-violet-300 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                      Active WFH Period
                    </p>
                    <p className="text-white/70 text-[11px] mt-0.5">
                      {activeWfhPeriod.from_date} to {activeWfhPeriod.to_date}
                    </p>
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-violet-500 text-white text-[10px] font-black uppercase tracking-tighter shadow-lg shadow-violet-900/40">
                  Approved
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Date + Status */}
          <div className="grid grid-cols-2 gap-3 sm:gap-5 mb-4 sm:mb-6">
            <div>
              <p className="text-[10px] sm:text-xs text-white/50 mb-1.5">📅 Date</p>
              <div
                className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-white/10 text-white
                font-medium border border-white/15 text-xs sm:text-sm truncate"
              >
                {today}
              </div>
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-white/50 mb-1.5">📊 Status</p>
              <div
                className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border flex items-center
                justify-between ${STATUS_STYLE[displayStatus] || STATUS_STYLE.ABSENT}`}
              >
                <span className="text-white/60 text-[10px] sm:text-xs hidden xs:block">Today</span>
                <span className="font-bold text-xs sm:text-sm w-full xs:w-auto text-center xs:text-right">
                  {STATUS_LABEL[displayStatus] || "ABSENT"}
                </span>
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <SummaryCard label="Check In" value={fmtTimeFromISO(rec.check_in)} icon="🕐" />
            <SummaryCard
              label="Check Out"
              value={checkedOut ? fmtTimeFromISO(rec.check_out) : "Pending"}
              icon="🕔"
            />
            <SummaryCard
              label="Duration"
              value={calcDuration(rec.check_in, rec.check_out)}
              icon="⌚"
            />
          </div>

          {/* Method badge */}
          {methodBadge && (
            <div className="flex justify-center mb-4 sm:mb-5">
              <span
                className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5
                rounded-full text-[10px] sm:text-xs font-semibold border ${methodBadge.cls}`}
              >
                {methodBadge.icon}
                <span className="hidden xs:inline">{methodBadge.label}</span>
                <span className="xs:hidden">{rec.method}</span>
                {rec.remote_approval_status && (
                  <span className="opacity-70">· {rec.remote_approval_status}</span>
                )}
              </span>
            </div>
          )}

          {/* Verification progress */}
          <AnimatePresence>
            {actionLoading && verifyStep && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 sm:mb-5 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-blue-500/10 border
                  border-blue-500/30 text-blue-300 text-xs sm:text-sm font-medium flex items-center gap-2"
              >
                <span className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                {verifyStep === "ip" ? "🌐 Verifying network…" : "📍 Trying GPS…"}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Half-day leave info banner — shown before check-in */}
          <AnimatePresence>
            {halfDayLeave && !checkedIn && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4"
              >
                <HalfDayLeaveBanner leave={halfDayLeave} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Soft block banner (HOLIDAY / WEEK_OFF — confirmation gate) */}
          <AnimatePresence>
            {isSoftBlocked && (
              <SoftBlockBanner
                softBlock={softBlock}
                onAcknowledge={() => setHolidayAcknowledged(true)}
              />
            )}
          </AnimatePresence>

          {/* Holiday work pending toast */}
          <AnimatePresence>
            {holidayPendingToast && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 rounded-2xl p-3.5 border flex items-start gap-2.5"
                style={{
                  background: "rgba(16,185,129,0.10)",
                  borderColor: "rgba(16,185,129,0.35)",
                }}
              >
                <span className="text-emerald-400 flex-shrink-0">✅</span>
                <p className="text-emerald-300 text-xs leading-relaxed">
                  <strong>Checked out!</strong> Your holiday work request has been submitted for admin approval.
                  Comp-off will be credited if approved.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hard block banner (LEAVE, REMOTE_PENDING) */}
          <AnimatePresence>
            {isHardBlocked && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4"
              >
                <BlockBanner
                  blockType={hardBlock.block_type}
                  reason={hardBlock.reason}
                />
                {/* Extra hint for calendar blocks */}
                {isCalendarBlock && (
                  <p className="text-center text-white/35 text-[10px] sm:text-xs mt-3">
                    Ask your admin to grant an override if you need to work today.
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Location error */}
          <AnimatePresence>
            {isLocationErrored && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-center"
              >
                <div className="text-2xl mb-2">📍</div>
                <p className="text-red-300 text-sm font-medium leading-relaxed mb-1">
                  {locationError}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Action buttons — only rendered when NOT hard-blocked AND NOT soft-blocked ── */}
          {!isHardBlocked && !isSoftBlocked && (
            <div className="flex flex-col items-center gap-3 sm:gap-5">
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 w-full">
                {!checkedIn ? (
                  <ActionBtn
                    label={
                      actionLoading
                        ? verifyStep === "gps"
                          ? "📍 GPS…"
                          : "🌐 Verifying…"
                        : "🕐 Check In"
                    }
                    color="from-emerald-500 to-teal-500"
                    disabled={actionLoading}
                    onClick={() => handleMark("check_in")}
                  />
                ) : (
                  <LockedBtn label="✓ Checked In" />
                )}

                {!checkedIn ? (
                  <LockedBtn label="🔒 Check Out" title="Check in first" />
                ) : checkedOut ? (
                  <LockedBtn label="✓ Checked Out" />
                ) : (
                  <ActionBtn
                    label={
                      actionLoading
                        ? verifyStep === "gps"
                          ? "📍 GPS…"
                          : "🌐 Verifying…"
                        : "🕔 Check Out"
                    }
                    color="from-orange-500 to-amber-500"
                    disabled={actionLoading}
                    onClick={() => handleMark("check_out")}
                  />
                )}
              </div>

              {/* Remote button */}
              <AnimatePresence>
                {showRemoteButton && (
                  <motion.div
                    key="remote-btn-wrapper"
                    initial={{ opacity: 0, y: 6, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="flex flex-col items-center gap-1"
                  >
                    <motion.button
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={openRemoteModal}
                      disabled={actionLoading}
                      className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl
                        text-xs sm:text-sm font-semibold transition-all duration-200 shadow-md
                        disabled:opacity-40 disabled:cursor-not-allowed
                        ${isLocationErrored
                          ? "text-cyan-200 border border-cyan-500/50 bg-gradient-to-r from-cyan-900/50 to-blue-900/50 hover:border-cyan-400/80 hover:text-white"
                          : "text-white/40 border border-white/10 hover:text-white/70 hover:border-white/25 hover:bg-white/[0.04]"
                        }`}
                    >
                      <span>🛰️</span>
                      <span>Remote Request</span>
                    </motion.button>
                    <p
                      className={`text-[10px] font-medium text-center px-2 ${isLocationErrored
                        ? "text-cyan-400/60"
                        : "text-white/20"
                        }`}
                    >
                      {isLocationErrored
                        ? "Can't reach office network — submit remote request"
                        : "Working off-site? Submit a remote request"}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {showRemote && (
          <RemoteModal
            action={pendingAction || (!record?.check_in ? "check_in" : "check_out")}
            today={today}
            onClose={() => setShowRemote(false)}
            onSuccess={() => {
              setShowRemote(false);
              setLocationError(null);
              loadAll(true);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  MAIN EXPORT
// ──────────────────────────────────────────────────────────────────────────────
export default function MarkAttendance() {
  const role = useAuthStore((state) => state.role);
  const navigate = useNavigate();
  const today = getLocalTodayString();

  if (role === "ADMIN") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl mx-auto px-2 sm:px-0"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 sm:p-10 shadow-2xl text-center">
          <div
            className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500
            to-indigo-600 flex items-center justify-center mx-auto mb-4 sm:mb-5 text-2xl sm:text-3xl shadow-lg"
          >
            🛡️
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Admin Portal</h3>
          <p className="text-white/60 mb-5 sm:mb-6 text-xs sm:text-sm leading-relaxed max-w-sm mx-auto">
            Admins do not mark personal attendance. Use the employee management
            section to view or manage records.
          </p>
          <button
            onClick={() => navigate("/admin/employees")}
            className="w-full sm:w-auto px-5 sm:px-6 py-3 rounded-xl font-semibold bg-gradient-to-r
              from-blue-500 to-indigo-600 text-white text-sm shadow-lg hover:opacity-90 transition"
          >
            Go to Employee Management
          </button>
        </div>
      </motion.div>
    );
  }

  return <AttendanceWidget role={role} today={today} navigate={navigate} />;
}
